import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '@/shared/contexts/TenantContext';
import { useAuth } from '@/shared/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  Building2, Check, Globe, LayoutGrid, Building, Warehouse, Store,
  Search, ArrowLeft, ChevronRight, LogOut, Package
} from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import { motion, AnimatePresence } from 'framer-motion';

const getOrgIcon = (type) => {
  switch (type) {
    case 'SITE': return <Warehouse className="h-5 w-5" />;
    case 'BUSINESS_UNIT': return <Building className="h-5 w-5" />;
    case 'DEPT': return <LayoutGrid className="h-5 w-5" />;
    case 'LEGAL_ENTITY': return <Store className="h-5 w-5" />;
    default: return <Building2 className="h-5 w-5" />;
  }
};

const ContextSelectionPage = () => {
  const {
    availableScopes,
    switchContext,
    activeContext
  } = useTenant();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation(['auth', 'common']);

  const [step, setStep] = React.useState('company'); // 'company' | 'unit'
  const [selectedTenant, setSelectedTenant] = React.useState(null);
  const [searchQuery, setSearchQuery] = React.useState('');

  const filteredTenants = React.useMemo(() => {
    if (!availableScopes?.tenants) return [];
    return availableScopes.tenants.filter(t =>
      t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.code.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [availableScopes, searchQuery]);

  const filteredUnits = React.useMemo(() => {
    if (!selectedTenant?.org_nodes) return [];
    return selectedTenant.org_nodes.filter(u =>
      u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.type.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [selectedTenant, searchQuery]);

  const handleTenantSelect = (tenant) => {
    if (tenant.org_nodes?.length === 1) {
      switchContext(tenant.id, tenant.org_nodes[0].id, '/erp');
    } else if (tenant.org_nodes?.length > 1) {
      setSelectedTenant(tenant);
      setStep('unit');
      setSearchQuery('');
    } else {
      switchContext(tenant.id, null, '/erp');
    }
  };

  const handleUnitSelect = (unit) => {
    switchContext(selectedTenant.id, unit.id, '/erp');
  };

  const resetSelection = () => {
    setStep('company');
    setSelectedTenant(null);
    setSearchQuery('');
  };

  return (
    <div className="min-h-screen bg-background text-foreground font-sans selection:bg-primary/30">
      {/* Background Decor */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary/5 blur-[120px] rounded-full" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-accent/5 blur-[120px] rounded-full" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-6 py-12 lg:py-20">
        {/* Header */}
        <header className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-2xl bg-gradient-to-br from-accent to-accent/80 shadow-lg shadow-accent/20">
              <Package className="h-8 w-8 text-accent-foreground" />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-foreground font-['Space_Grotesk'] italic leading-none">EzERP</h1>
              <p className="text-muted-foreground text-[10px] font-bold tracking-widest uppercase mt-1">{t('auth:securityLayer', 'Enterprise Security Layer')}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              className="bg-card border-border text-muted-foreground hover:text-foreground transition-all"
              onClick={logout}
            >
              <LogOut className="h-4 w-4 mr-2" />
              {t('common:signOut', 'Sign Out')}
            </Button>
          </div>
        </header>

        {/* Main Selection Area */}
        <main>
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-4xl font-bold tracking-tight mb-2">
                  {step === 'company' ? t('auth:selectCompany', 'Select Company') : t('auth:selectUnit', 'Select Business Unit')}
                </h2>
                <p className="text-muted-foreground text-lg">
                  {step === 'company'
                    ? t('auth:welcomeBack', 'Welcome back. Choose the client context you wish to manage today.')
                    : t('auth:drillingDown', 'Drilling down into {{name}}. Select the operational unit.', { name: selectedTenant?.name })}
                </p>
              </div>
              {step === 'unit' && (
                <Button
                  onClick={resetSelection}
                  variant="ghost"
                  className="text-primary hover:bg-primary/10 font-bold"
                >
                  <ArrowLeft className="h-5 w-5 mr-2" />
                  Change Company
                </Button>
              )}
            </div>

            {/* Search Bar */}
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-muted-foreground group-focus-within:text-primary transition-colors" />
              </div>
              <Input
                type="text"
                placeholder={step === 'company' ? t('common:searchCompanies', "Search client companies...") : t('common:searchUnits', "Search units...")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-card border-border h-16 pl-12 text-lg rounded-2xl focus-visible:ring-primary focus-visible:border-primary transition-all shadow-sm"
              />
            </div>
          </div>

          {/* Grid / List */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
            <AnimatePresence mode="wait">
              {step === 'company' ? (
                filteredTenants.map((tenant, idx) => (
                  <motion.div
                    key={tenant.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                  >
                    <Card
                      className={`group relative overflow-hidden bg-card border-border hover:border-primary/50 transition-all duration-300 cursor-pointer hover:shadow-xl active:scale-[0.98] ${activeContext?.client_id === tenant.id ? 'ring-2 ring-primary border-primary' : ''}`}
                      onClick={() => handleTenantSelect(tenant)}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-5">
                            <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center border border-border group-hover:bg-primary group-hover:border-primary transition-all duration-500">
                              <Globe className="h-8 w-8 text-muted-foreground group-hover:text-primary-foreground" />
                            </div>
                            <div>
                              <h3 className="text-xl font-bold group-hover:text-primary transition-colors">{tenant.name}</h3>
                              <p className="text-muted-foreground font-mono text-[10px] uppercase tracking-widest">{tenant.code}</p>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <Badge variant="secondary" className="bg-muted text-muted-foreground font-bold px-3 py-1">
                              {tenant.org_nodes?.length || 0} UNITS
                            </Badge>
                            <ChevronRight className="h-6 w-6 text-muted shadow-sm group-hover:text-primary group-hover:translate-x-1 transition-all" />
                          </div>
                        </div>
                      </CardContent>
                      {activeContext?.client_id === tenant.id && (
                        <div className="absolute top-0 right-0 p-2">
                          <div className="bg-primary p-1 rounded-bl-xl shadow-lg">
                            <Check className="h-4 w-4 text-primary-foreground" />
                          </div>
                        </div>
                      )}
                    </Card>
                  </motion.div>
                ))
              ) : (
                filteredUnits.map((unit, idx) => (
                  <motion.div
                    key={unit.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: idx * 0.05 }}
                  >
                    <Card
                      className={`group bg-card border-border hover:border-primary/50 hover:bg-muted/50 transition-all cursor-pointer ${activeContext?.business_id === unit.id ? 'ring-2 ring-primary/50 bg-primary/5 border-primary/40' : ''}`}
                      onClick={() => handleUnitSelect(unit)}
                    >
                      <CardContent className="p-6 flex items-center justify-between">
                        <div className="flex items-center gap-5">
                          <div className={`p-4 rounded-xl bg-muted border border-border group-hover:bg-primary/10 group-hover:text-primary transition-colors ${activeContext?.business_id === unit.id ? 'bg-primary/20 text-primary border-primary/20' : 'text-muted-foreground'}`}>
                            {getOrgIcon(unit.type)}
                          </div>
                          <div>
                            <h4 className="text-lg font-bold group-hover:text-primary transition-colors">{unit.name}</h4>
                            <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground group-hover:text-primary/70">{unit.type}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {activeContext?.business_id === unit.id && (
                            <div className="flex items-center gap-1 text-primary text-xs font-black uppercase tracking-tighter">
                              <Check className="h-4 w-4" />
                              Active
                            </div>
                          )}
                          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <ChevronRight className="h-5 w-5 text-primary" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))
              )}
            </AnimatePresence>
          </div>

          {/* Empty States */}
          {((step === 'company' && filteredTenants.length === 0) || (step === 'unit' && filteredUnits.length === 0)) && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="py-32 text-center"
            >
              <div className="mb-4 flex justify-center">
                <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center border border-border">
                  <Search className="h-8 w-8 text-muted-foreground" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-muted-foreground">{t('common:noResults', 'No results found')}</h3>
              <p className="text-muted-foreground/60 mb-6">{t('common:noResultsDesc', 'We couldn\'t find any authorized scopes matching')} "{searchQuery}"</p>
              <Button
                onClick={() => setSearchQuery('')}
                className="bg-primary hover:bg-primary/90"
              >
                {t('common:clearSearch', 'Clear Search & Try Again')}
              </Button>
            </motion.div>
          )}
        </main>

        {/* Footer Info */}
        <footer className="mt-20 pt-8 border-t border-border flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-muted-foreground text-[10px] font-medium uppercase tracking-[0.2em]">
            EzERP Enterprise Security Architecture • {new Date().getFullYear()}
          </p>
          <div className="flex items-center gap-3 grayscale opacity-40 hover:grayscale-0 hover:opacity-100 transition-all cursor-default">
            <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-[10px] font-bold text-muted-foreground">Environment: PRODUCTION_LOCKED</span>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default ContextSelectionPage;
